/**
 * AccountSection - Displays account status and usage information
 * This is read-only information about the user's current plan and usage
 */

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { Progress } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useUserLimits } from '@/hooks/useUserLimits'
import { useUtcMidnightCountdown } from '@/hooks/useUtcMidnightCountdown'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useBoostStatus } from '@/hooks/useBoostStatus'
import { useBoostRefund } from '@/hooks/useBoostRefund'
import { useRefundRequestStatus } from '@/hooks/useRefundRequestStatus'
import { useToast } from '@/components/ui/enhanced-toast'
import { BoostStatusCard } from '@/components/settings/BoostStatusCard'
import { BoostRefundDialog } from '@/components/settings/BoostRefundDialog'
import { cn } from '@/utils/cn'
import { AlertCircle, CreditCard, ExternalLink, Info, Mail, RefreshCw, User, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const numberFormatter = new Intl.NumberFormat('en-US')

export function AccountSection() {
  const { data: limits, isLoading, isRefetching, error, refetch } = useUserLimits()
  const { formattedRemaining } = useUtcMidnightCountdown()
  const { userInfo } = useAuth()

  // Boost refund state
  const { value: boostEnabled, status: flagStatus } = useFeatureFlag("optimizer.trader_max_boost")
  const { activeBoost, recentBoost } = useBoostStatus(boostEnabled, flagStatus)
  const { submitRefund, isPending: isRefundPending, isSuccess: isRefundSuccess, isAlreadyRequested, reset: resetRefund } = useBoostRefund()
  const { hasPendingRequest } = useRefundRequestStatus(
    activeBoost?.purchasedAt ?? recentBoost?.purchasedAt ?? null,
    activeBoost?.expiresAt ?? recentBoost?.expiresAt ?? null,
  )
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  const toast = useToast()

  // Handle refund submission outcomes
  useEffect(() => {
    if (isRefundSuccess) {
      setRefundDialogOpen(false)
      toast.success("Refund request submitted", "We'll review it and follow up within 3 business days.")
      resetRefund()
    }
  }, [isRefundSuccess, toast, resetRefund])

  useEffect(() => {
    if (isAlreadyRequested) {
      setRefundDialogOpen(false)
      toast.info("Already submitted", "A refund request for this boost has already been submitted.")
      resetRefund()
    }
  }, [isAlreadyRequested, toast, resetRefund])

  if (isLoading) {
    return <AccountSectionSkeleton />
  }

  const dailyLimit = Math.max(0, limits?.daily_trial_limit ?? 100)
  const usedToday = Math.max(0, limits?.trials_used_today ?? 0)
  const reserved = Math.max(0, limits?.credits_reserved ?? 0)
  const creditsAvailable = limits?.credits_available
  const remaining = Math.max(
    0,
    typeof creditsAvailable === "number" ? creditsAvailable : dailyLimit - usedToday - reserved
  )
  const isMaxTrialActive = userInfo?.maxTrialActive === true
  const effectiveTier = limits?.tier === 'max' ? 'MAX' : (limits?.tier?.toUpperCase() || 'FREE')
  // When boost is active, show the user's real subscription tier, not the boosted tier
  const tierLabel = isMaxTrialActive ? (limits?.tier === 'max' ? 'PRO' : effectiveTier) : effectiveTier
  const percentageRemaining = dailyLimit > 0 ? (remaining / dailyLimit) * 100 : 100

  return (
    <div className="space-y-6">
      {/* Profile Info */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <User className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <GlassCardTitle className="text-lg">Profile</GlassCardTitle>
              <GlassCardDescription>Your account information</GlassCardDescription>
            </div>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-border/30">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4" />
                Email
              </div>
              <span className="text-sm font-medium text-foreground">
                {userInfo?.email || 'Not available'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CreditCard className="h-4 w-4" />
                Subscription Tier
              </div>
              <div className="flex items-center gap-2">
                <div className={cn(
                  "px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border",
                  !isMaxTrialActive && tierLabel === 'MAX'
                    ? "bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 text-amber-300"
                    : tierLabel === 'PRO'
                    ? "bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-300"
                    : "bg-zinc-800/50 border-white/10 text-zinc-400"
                )}>
                  {tierLabel}
                </div>
                {isMaxTrialActive && (
                  <div className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 text-amber-300 animate-pulse">
                    Max Boost
                  </div>
                )}
              </div>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Usage Card */}
      <GlassCard className="relative overflow-hidden">
        {/* Error Overlay */}
        {error && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 text-center">
            <div className="space-y-2">
              <AlertCircle className="w-8 h-8 text-amber-500 mx-auto" />
              <p className="text-xs text-amber-200">Failed to sync credits</p>
              <Button size="sm" variant="outline" className="h-7 text-xs border-amber-500/50 text-amber-500 hover:bg-amber-500/10" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          </div>
        )}

        <GlassCardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <Zap className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <GlassCardTitle className="text-lg">Usage</GlassCardTitle>
                <GlassCardDescription>Daily credit allocation and consumption</GlassCardDescription>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => refetch()}
                    className={cn(
                      "p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors",
                      isRefetching && "animate-spin"
                    )}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Refresh usage data</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="space-y-6">
            {/* Main usage display */}
            <div className="flex items-baseline justify-between">
              <div>
                <span className={cn(
                  "text-4xl font-bold tracking-tight text-white tabular-nums",
                  isRefetching && "opacity-70 transition-opacity"
                )}>
                  {numberFormatter.format(remaining)}
                </span>
                <span className="text-sm text-zinc-500 font-medium ml-2">
                  / {numberFormatter.format(dailyLimit)} credits remaining
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <Progress
                value={percentageRemaining}
                className="h-2 bg-white/5"
                variant={remaining < (dailyLimit * 0.1) ? "danger" : "brand"}
              />
              <div className="flex justify-between text-xs text-zinc-500 font-medium">
                <span>0%</span>
                <span className="text-zinc-400">Available capacity</span>
                <span>100%</span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Used Today</div>
                <div className="text-lg font-semibold text-zinc-200 tabular-nums">
                  {numberFormatter.format(usedToday)}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Max Parallel</div>
                <div className="flex items-center gap-2 text-lg font-semibold text-zinc-200">
                  <Zap className={cn(
                    "w-4 h-4",
                    (tierLabel === 'MAX' || tierLabel === 'PRO')
                      ? "text-amber-400 fill-amber-400/20"
                      : "text-zinc-600"
                  )} />
                  {limits?.max_parallel_studies ?? 1}x
                </div>
              </div>
            </div>

            {/* Info footer */}
            <div className="flex items-center gap-2 text-xs text-zinc-500 pt-2 border-t border-border/30">
              <Info className="h-3.5 w-3.5" />
              Credits reset in <span className="tabular-nums text-zinc-400">{formattedRemaining}</span> (midnight UTC)
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Boost / Upgrade section — 4-state machine */}
      {(() => {
        const refundAlreadySubmitted = hasPendingRequest
          || (activeBoost as any)?.refundRequested === true
          || (recentBoost?.refundRequested ?? false);

        // State 2: Active boost — show status card with refund link
        if (activeBoost != null) {
          return (
            <BoostStatusCard
              purchasedAt={activeBoost.purchasedAt}
              expiresAt={activeBoost.expiresAt}
              onRequestRefund={() => setRefundDialogOpen(true)}
              refundRequested={refundAlreadySubmitted}
            />
          );
        }

        // TODO(AIE-357): Cooldown state — render when cooldownUntil is future.
        // Cooldown and refund eligibility can overlap; both must be visible.
        // AccountSection already has cooldownUntil/boostChainCount via useBoostStatus;
        // the follow-up is a state-machine/design decision, not missing data plumbing.

        // State 3: Expired, purchase within 14-day refund window (gated on AIEW recentBoost)
        if (recentBoost != null && recentBoost.refundEligible && !refundAlreadySubmitted) {
          return (
            <>
              {tierLabel !== 'MAX' && <UpgradeCTA boostEligible={userInfo?.boostEligible === true} />}
              <p className="text-sm text-muted-foreground mt-2">
                Your Trader Max Boost expired on{' '}
                {new Date(recentBoost.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}.{' '}
                <button
                  onClick={() => setRefundDialogOpen(true)}
                  className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                >
                  Request a refund
                </button>
              </p>
            </>
          );
        }

        // State 1/4: No boost or expired past window
        if (tierLabel !== 'MAX' && !isMaxTrialActive) {
          return <UpgradeCTA boostEligible={userInfo?.boostEligible === true} />;
        }

        return null;
      })()}

      {/* Refund dialog — shared by States 2 and 3 */}
      <BoostRefundDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        onSubmit={(reason) => {
          const boostData = activeBoost ?? recentBoost;
          if (!boostData) return;
          // Truncate to seconds to match backend _parse_ts() precision
          const truncSec = (d: Date) => new Date(Math.floor(d.getTime() / 1000) * 1000);
          const purchasedAt = truncSec(new Date(boostData.purchasedAt)).toISOString();
          const expiresAt = truncSec(new Date(boostData.expiresAt)).toISOString();
          const durationDays = Math.round((new Date(expiresAt).getTime() - new Date(purchasedAt).getTime()) / 86_400_000);
          submitRefund({ reason, purchased_at: purchasedAt, expires_at: expiresAt, duration_days: durationDays });
        }}
        isPending={isRefundPending}
      />
    </div>
  )
}

function UpgradeCTA({ boostEligible }: { boostEligible: boolean }) {
  const navigate = useNavigate()
  const { value: boostEnabled } = useFeatureFlag("optimizer.trader_max_boost")
  // Uses JWT boost_eligible claim (client-side) rather than calling /boost/status
  // to avoid a network call on Settings for all users. The /boost page does the
  // authoritative server-side eligibility check. See design doc for rationale.
  const showBoostSection = boostEligible && boostEnabled

  return (
    <GlassCard className="border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-orange-500/5">
      <GlassCardContent className="py-5">
        {showBoostSection ? (
          /* Boost-eligible layout: vertically stacked sections with "or" divider */
          <div className="space-y-5">
            {/* Unlock More Power */}
            <div className="space-y-3">
              <h4 className="font-semibold text-amber-200">Unlock More Power</h4>
              <p className="text-sm text-muted-foreground">
                Upgrade to MAX for 100,000 daily trials and 10x parallel studies.
              </p>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                  onClick={() => window.open('https://www.patreon.com/jdehorty/membership', '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Patreon Plans
                </Button>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-amber-500/20" />
              <span className="text-xs text-muted-foreground font-medium">or</span>
              <div className="flex-1 border-t border-amber-500/20" />
            </div>

            {/* Temporary boost option */}
            <div className="space-y-3">
              <h4 className="font-semibold text-amber-200">Trader Max Boost</h4>
              <p className="text-sm text-muted-foreground">
                Try Max-level capabilities with a time-limited boost, without changing your current plan.
              </p>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                  onClick={() => navigate('/boost')}
                >
                  View Add-ons
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Default layout: horizontal upgrade CTA */
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="font-semibold text-amber-200">Unlock More Power</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Upgrade to MAX for 100,000 daily trials and 10x parallel studies.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
              onClick={() => window.open('https://www.patreon.com/jdehorty/membership', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Patreon Plans
            </Button>
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  )
}

function AccountSectionSkeleton() {
  return (
    <div className="space-y-6">
      <GlassCard>
        <div className="p-6 space-y-4">
          <div className="h-6 w-24 bg-white/10 rounded animate-pulse" />
          <div className="space-y-3">
            <div className="h-10 bg-white/5 rounded animate-pulse" />
            <div className="h-10 bg-white/5 rounded animate-pulse" />
          </div>
        </div>
      </GlassCard>
      <GlassCard>
        <div className="p-6 space-y-4">
          <div className="h-6 w-20 bg-white/10 rounded animate-pulse" />
          <div className="h-12 w-40 bg-white/10 rounded animate-pulse" />
          <div className="h-2 bg-white/10 rounded-full animate-pulse" />
          <div className="grid grid-cols-2 gap-4 h-20">
            <div className="bg-white/5 rounded animate-pulse" />
            <div className="bg-white/5 rounded animate-pulse" />
          </div>
        </div>
      </GlassCard>
    </div>
  )
}
